import React from 'react';
import { Calendar, Filter, X } from 'lucide-react';

const ReportFilters = ({ 
  currentRole, 
  categories, 
  selectedCategory, 
  onCategoryChange,
  dateRange,
  onDateRangeChange,
  customFilters,
  onCustomFiltersChange,
  formatDate
}) => {
  const handleClearFilters = () => {
    onCategoryChange('all');
    onDateRangeChange({ from: '', to: '' });
    onCustomFiltersChange({});
  };

  const hasActiveFilters = selectedCategory !== 'all' || dateRange?.from || dateRange?.to || Object.keys(customFilters || {})?.length > 0;

  // Format a YYYY-MM-DD string to DD MMM YYYY for display
  const displayDate = (isoDate) => {
    if (!isoDate) return '';
    if (formatDate) return formatDate(isoDate);
    const d = new Date(isoDate);
    if (isNaN(d?.getTime())) return isoDate;
    const day = String(d?.getDate())?.padStart(2, '0');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${day} ${months?.[d?.getMonth()]} ${d?.getFullYear()}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-[#1e4a72]" />
          <h3 className="text-lg font-semibold text-gray-900">Filter Reports</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            Clear All Filters
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Report Category
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e4a72] focus:border-transparent transition-all"
          >
            <option value="all">All Categories</option>
            {categories?.map((category) => (
              <option key={category?.id} value={category?.id}>
                {category?.name}
              </option>
            ))}
          </select>
        </div>

        {/* Date From */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            From Date
          </label>
          <div className="relative">
            {dateRange?.from && (
              <div className="absolute inset-0 flex items-center px-4 pointer-events-none z-10 bg-white rounded-lg border border-gray-300 text-sm text-gray-800">
                {displayDate(dateRange?.from)}
              </div>
            )}
            <input
              type="date"
              value={dateRange?.from || ''}
              onChange={(e) => onDateRangeChange({ ...dateRange, from: e?.target?.value })}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e4a72] focus:border-transparent transition-all ${dateRange?.from ? 'opacity-0' : ''}`}
            />
          </div>
        </div>

        {/* Date To */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            To Date
          </label>
          <div className="relative">
            {dateRange?.to && (
              <div className="absolute inset-0 flex items-center px-4 pointer-events-none z-10 bg-white rounded-lg border border-gray-300 text-sm text-gray-800">
                {displayDate(dateRange?.to)}
              </div>
            )}
            <input
              type="date"
              value={dateRange?.to || ''}
              onChange={(e) => onDateRangeChange({ ...dateRange, to: e?.target?.value })}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e4a72] focus:border-transparent transition-all ${dateRange?.to ? 'opacity-0' : ''}`}
            />
          </div>
        </div>

        {/* Quick Date Ranges */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Select
          </label>
          <select
            onChange={(e) => {
              const today = new Date();
              let from = '';
              
              switch(e?.target?.value) {
                case 'today':
                  from = today?.toISOString()?.split('T')?.[0];
                  onDateRangeChange({ from, to: from });
                  break;
                case 'week': {
                  const weekAgo = new Date();
                  weekAgo?.setDate(weekAgo?.getDate() - 7);
                  from = weekAgo?.toISOString()?.split('T')?.[0];
                  onDateRangeChange({ from, to: new Date()?.toISOString()?.split('T')?.[0] });
                  break;
                }
                case 'month': {
                  const monthAgo = new Date();
                  monthAgo?.setMonth(monthAgo?.getMonth() - 1);
                  from = monthAgo?.toISOString()?.split('T')?.[0];
                  onDateRangeChange({ from, to: new Date()?.toISOString()?.split('T')?.[0] });
                  break;
                }
                case 'quarter': {
                  const quarterAgo = new Date();
                  quarterAgo?.setMonth(quarterAgo?.getMonth() - 3);
                  from = quarterAgo?.toISOString()?.split('T')?.[0];
                  onDateRangeChange({ from, to: new Date()?.toISOString()?.split('T')?.[0] });
                  break;
                }
                case 'year': {
                  const yearAgo = new Date();
                  yearAgo?.setFullYear(yearAgo?.getFullYear() - 1);
                  from = yearAgo?.toISOString()?.split('T')?.[0];
                  onDateRangeChange({ from, to: new Date()?.toISOString()?.split('T')?.[0] });
                  break;
                }
                default:
                  break;
              }
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e4a72] focus:border-transparent transition-all"
          >
            <option value="">Select Range</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-2">Active Filters:</p>
          <div className="flex flex-wrap gap-2">
            {selectedCategory !== 'all' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                Category: {categories?.find(c => c?.id === selectedCategory)?.name}
                <button
                  onClick={() => onCategoryChange('all')}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {dateRange?.from && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                From: {displayDate(dateRange?.from)}
                <button
                  onClick={() => onDateRangeChange({ ...dateRange, from: '' })}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {dateRange?.to && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                To: {displayDate(dateRange?.to)}
                <button
                  onClick={() => onDateRangeChange({ ...dateRange, to: '' })}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportFilters;